import { useState } from "react";
import { toast } from "sonner";
import { apiClient, publicApi } from "@/lib/api";
import { Loader2, X, MapPin } from "lucide-react";

const money = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;

const EXPIRY_OPTIONS = [
  { key: "24", label: "24 hours", hours: 24 },
  { key: "48", label: "48 hours", hours: 48 },
  { key: "168", label: "7 days", hours: 168 },
  { key: "0", label: "No expiry", hours: 0 },
  { key: "custom", label: "Custom (hours)", hours: null },
];

export default function ManualQuoteForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    preferred_contact: "phone",
    vehicle_registration: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    vehicle_size: "standard",
    condition: "runner",
    lowered: "no",
    pickup_location: "",
    dropoff_location: "",
    requested_collection_date: "",
    requested_collection_time: "flexible",
    agreed_transport_price: "",
    customer_notes: "",
    internal_notes: "",
  });
  const [expiryKey, setExpiryKey] = useState("24");
  const [customHours, setCustomHours] = useState("48");
  const [loadedMiles, setLoadedMiles] = useState(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function set(k, v) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  async function computeDistance() {
    if (!form.pickup_location.trim() || !form.dropoff_location.trim()) {
      toast.error("Enter both pickup and delivery addresses.");
      return;
    }
    setDistanceLoading(true);
    try {
      const { data } = await publicApi.post("/distance", {
        pickup: form.pickup_location,
        dropoff: form.dropoff_location,
      });
      if (data.miles != null) {
        setLoadedMiles(data.miles);
        toast.success(`Loaded mileage: ${data.miles} mi`);
      } else {
        toast.info("Route lookup returned no mileage.");
      }
    } catch (e) {
      toast.error("Distance lookup failed. Enter miles manually if needed.");
    } finally {
      setDistanceLoading(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    const price = parseFloat(form.agreed_transport_price);
    if (!form.customer_name.trim() || !form.customer_email.trim() || !form.customer_phone.trim()) {
      toast.error("Customer name, email and phone are required.");
      return;
    }
    if (!form.pickup_location.trim() || !form.dropoff_location.trim()) {
      toast.error("Pickup and delivery addresses are required.");
      return;
    }
    if (!price || price <= 0) {
      toast.error("Enter a valid agreed price.");
      return;
    }
    const opt = EXPIRY_OPTIONS.find((o) => o.key === expiryKey);
    let expiry_hours = opt.hours;
    if (expiryKey === "custom") {
      const h = parseInt(customHours, 10);
      if (Number.isNaN(h) || h < 0) {
        toast.error("Enter a valid custom expiry in hours.");
        return;
      }
      expiry_hours = h;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        agreed_transport_price: price,
        loaded_miles: loadedMiles != null ? Number(loadedMiles) : null,
        vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year, 10) : null,
        expiry_hours,
      };
      const { data } = await apiClient.post("/admin/bookings", payload);
      toast.success(`Manual quote ${data.booking_ref} created.`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(", ")
          : "Could not create manual quote.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // Live deposit + balance preview
  const previewPrice = parseFloat(form.agreed_transport_price) || 0;
  const previewDeposit = previewPrice > 0 ? Math.ceil((previewPrice * 0.30) / 10) * 10 : 0;
  const previewBalance = Math.max(0, previewPrice - previewDeposit);

  return (
    <Backdrop onClose={onClose}>
      <form
        data-testid="admin-manual-quote-form"
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl border border-white/15 bg-[#0A0A0A] p-6 md:p-8 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
              Create Manual Quote
            </div>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl mt-2">
              New off-platform booking
            </h2>
            <p className="text-zinc-400 text-sm mt-2 max-w-xl">
              For customers contacting via phone, WhatsApp, Instagram or email. Website pricing is not used — you type the agreed price directly.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="admin-manual-quote-close">
            <X className="w-5 h-5 text-zinc-400 hover:text-white" />
          </button>
        </div>

        <Section title="Customer">
          <Row>
            <Field label="Full name" required><Input testid="manual-name" value={form.customer_name} onChange={(v) => set("customer_name", v)} /></Field>
            <Field label="Phone" required><Input testid="manual-phone" value={form.customer_phone} onChange={(v) => set("customer_phone", v)} /></Field>
          </Row>
          <Row>
            <Field label="Email" required><Input testid="manual-email" type="email" value={form.customer_email} onChange={(v) => set("customer_email", v)} /></Field>
            <Field label="Preferred contact">
              <Select testid="manual-contact" value={form.preferred_contact} onChange={(v) => set("preferred_contact", v)}
                options={[["phone","Phone"],["whatsapp","WhatsApp"],["email","Email"]]} />
            </Field>
          </Row>
        </Section>

        <Section title="Vehicle">
          <Row>
            <Field label="Registration"><Input testid="manual-reg" value={form.vehicle_registration} onChange={(v) => set("vehicle_registration", v.toUpperCase())} /></Field>
            <Field label="Year"><Input testid="manual-year" type="number" value={form.vehicle_year} onChange={(v) => set("vehicle_year", v)} /></Field>
          </Row>
          <Row>
            <Field label="Make"><Input testid="manual-make" value={form.vehicle_make} onChange={(v) => set("vehicle_make", v)} /></Field>
            <Field label="Model"><Input testid="manual-model" value={form.vehicle_model} onChange={(v) => set("vehicle_model", v)} /></Field>
          </Row>
          <Row>
            <Field label="Size">
              <Select testid="manual-size" value={form.vehicle_size} onChange={(v) => set("vehicle_size", v)}
                options={[["standard","Standard"],["large","Large / SUV"],["prestige","Prestige"],["classic","Classic"]]} />
            </Field>
            <Field label="Condition">
              <Select testid="manual-condition" value={form.condition} onChange={(v) => set("condition", v)}
                options={[["runner","Runner"],["non_runner","Non-runner"],["seized","Seized"],["damaged","Damaged"]]} />
            </Field>
          </Row>
        </Section>

        <Section title="Journey">
          <Row>
            <Field label="Collection address" required><Input testid="manual-pickup" value={form.pickup_location} onChange={(v) => set("pickup_location", v)} placeholder="Postcode or full address" /></Field>
            <Field label="Delivery address" required><Input testid="manual-dropoff" value={form.dropoff_location} onChange={(v) => set("dropoff_location", v)} placeholder="Postcode or full address" /></Field>
          </Row>
          <div className="flex items-center gap-3 -mt-2 mb-4">
            <button
              type="button"
              data-testid="manual-distance-btn"
              onClick={computeDistance}
              disabled={distanceLoading}
              className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-300 hover:border-[#E60000] hover:text-white transition-colors disabled:opacity-50"
            >
              {distanceLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
              Lookup mileage
            </button>
            {loadedMiles != null && (
              <span data-testid="manual-loaded-miles" className="text-xs text-zinc-400">
                Loaded mileage: <span className="text-white font-bold">{loadedMiles} mi</span>
                <span className="text-zinc-600 ml-2">(reference only — does not change your price)</span>
              </span>
            )}
          </div>
          <Row>
            <Field label="Collection date"><Input testid="manual-date" type="date" value={form.requested_collection_date} onChange={(v) => set("requested_collection_date", v)} /></Field>
            <Field label="Collection time">
              <Select testid="manual-time" value={form.requested_collection_time} onChange={(v) => set("requested_collection_time", v)}
                options={[["flexible","Flexible / anytime"],["day","Daytime (09-17)"],["early","Early (06-09)"],["evening","Evening (17-21)"],["late","Late (21-00)"],["overnight","Overnight"]]} />
            </Field>
          </Row>
        </Section>

        <Section title="Agreed price & expiry">
          <Row>
            <Field label="Agreed transport price (£)" required>
              <Input testid="manual-price" type="number" step="0.01" value={form.agreed_transport_price} onChange={(v) => set("agreed_transport_price", v)} placeholder="e.g. 350" />
            </Field>
            <Field label="Quote expiry">
              <Select testid="manual-expiry" value={expiryKey} onChange={setExpiryKey}
                options={EXPIRY_OPTIONS.map((o) => [o.key, o.label])} />
            </Field>
          </Row>
          {expiryKey === "custom" && (
            <Row>
              <Field label="Custom expiry (hours)">
                <Input testid="manual-expiry-custom" type="number" min={1} value={customHours} onChange={setCustomHours} />
              </Field>
              <div />
            </Row>
          )}
          {previewPrice > 0 && (
            <div data-testid="manual-preview" className="grid grid-cols-3 gap-3 mt-2">
              <Stat label="Agreed price" value={money(previewPrice)} />
              <Stat label="Deposit (30%, £10 round-up)" value={money(previewDeposit)} highlight />
              <Stat label="Balance remaining" value={money(previewBalance)} />
            </div>
          )}
        </Section>

        <Section title="Notes">
          <Field label="Customer notes / instructions (shown to driver only, not on customer quote)">
            <Textarea testid="manual-cust-notes" value={form.customer_notes} onChange={(v) => set("customer_notes", v)} placeholder="Keys at reception · Ask for John · Access instructions" />
          </Field>
          <Field label="Internal notes (admin-only)">
            <Textarea testid="manual-int-notes" value={form.internal_notes} onChange={(v) => set("internal_notes", v)} placeholder="Backload from Glasgow · Driver offered £270 · Repeat client" />
          </Field>
        </Section>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            data-testid="admin-manual-quote-cancel"
            onClick={onClose}
            className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold text-zinc-300 hover:border-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="admin-manual-quote-submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold text-white transition-colors disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create manual quote
          </button>
        </div>
      </form>
    </Backdrop>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center overflow-y-auto py-8 px-4"
      onClick={onClose}
    >
      {children}
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="mb-6">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}
function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">
        {label}{required && <span className="text-[#E60000]"> *</span>}
      </div>
      {children}
    </label>
  );
}
function Input({ testid, value, onChange, type = "text", placeholder, ...rest }) {
  return (
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000]"
      {...rest}
    />
  );
}
function Textarea({ testid, value, onChange, placeholder }) {
  return (
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#E60000] resize-none"
    />
  );
}
function Select({ testid, value, onChange, options }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border border-white/15 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E60000] appearance-none cursor-pointer"
    >
      {options.map(([v, l]) => (
        <option key={v} className="bg-[#0A0A0A]" value={v}>{l}</option>
      ))}
    </select>
  );
}
function Stat({ label, value, highlight }) {
  return (
    <div className={`border ${highlight ? "border-[#E60000]/40" : "border-white/10"} bg-[#050505] p-3`}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">{label}</div>
      <div className={`font-display text-lg font-bold mt-1 ${highlight ? "text-[#E60000]" : "text-white"}`}>{value}</div>
    </div>
  );
}
